import { InvoiceMaterialPrices } from "@/components/MaterialPriceUpdates";
import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { SupplierItems, OriginalInvoice } from "@/components/invoices/SupplierItems";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "./_shared";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Upload, FileText, AlertTriangle, CheckCircle2, Sparkles, Loader2, Trash2,
  ExternalLink, Building2, DollarSign, Hash, Calendar, Wand2,
} from "lucide-react";
import { toast } from "sonner";

const fmt = (n, cur = "NZD") => {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return `${cur === "NZD" ? "$" : (cur === "USD" ? "$" : cur + " ")}${v.toFixed(2)}`;
};

function StatusPill({ status }) {
  const map = {
    auto_linked:    { label: "Auto-linked", cls: "bg-green-600 text-white" },
    confirmed:      { label: "Confirmed",  cls: "bg-[#FF5F15] text-white" },
    pending_review: { label: "Review",     cls: "bg-yellow-400 text-black" },
    archived:       { label: "Archived",   cls: "bg-zinc-300 text-zinc-700" },
  };
  const m = map[status] || { label: status || "—", cls: "bg-zinc-200 text-zinc-700" };
  return <span className={`text-[11px] font-bold uppercase tracking-widest px-2 py-1 ${m.cls}`}>{m.label}</span>;
}

export default function Invoices() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const sourceJobId = searchParams.get("job_id");
  const [items, setItems] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [priceReviewInvoice, setPriceReviewInvoice] = useState(null);
  const [reviewState, setReviewState] = useState(null); // {ai, file, matched_job, match_kind}
  const fileRef = useRef(null);
  const manualRoute = `/app/invoicing/invoices/manual${sourceJobId ? `?job_id=${encodeURIComponent(sourceJobId)}` : ""}`;
  const [saving, setSaving] = useState(false);
  const [fleet, setFleet] = useState(null);
  useEffect(() => {
    if (!reviewOpen) return;
    let active = true;
    setFleet(null);
    api.get("/fleet").then(({ data }) => { if (active) setFleet((data || []).filter((asset) => asset.org_id === (reviewState?.ai?.org_id || user?.org_id))); }).catch(() => {});
    return () => { active = false; };
  // Only load on opening an invoice; edits use the same read-only fleet list.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewOpen]);
  const editInvoice = (invoice) => {
    setReviewState({ id: invoice.id, ai: { ...invoice }, original: invoice.ai_data,
      file: { path: invoice.file_path }, job_id: invoice.job_id || "", operational_reviewed: false });
    setReviewOpen(true);
  };

  const load = async () => {
    try {
      const [inv, jb] = await Promise.all([
        api.get("/invoices"),
        api.get("/jobs"),
      ]);
      setItems(inv.data || []);
      const requested = (inv.data || []).find((item) => item.id === searchParams.get("review"));
      if (requested) editInvoice(requested);
      setJobs(jb.data || []);
    } catch (e) {
      toast.error("Failed to load invoices");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const onPick = () => fileRef.current?.click();
  const [dragOver, setDragOver] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 12 * 1024 * 1024) { toast.error("File too large (max 12MB)"); return; }
    setScanning(true);
    const toastId = toast.loading("Scanning invoice with Claude Sonnet 4.5…");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/invoices/scan", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,  // 3 minutes — Claude vision can take a while on PDFs
      });
      toast.dismiss(toastId);
      // Pre-fill the review modal. If match_kind === "number" AND a job was
      // found, the office can hit "Confirm & link" in one click.
      setReviewState({
        ai: data.ai || {},
        file: data.file,
        matched_job: data.matched_job,
        match_kind: data.match_kind,
        job_id: sourceJobId || data.matched_job?.id || "",
        original: data.ai,
        operational_reviewed: false,
      });
      setReviewOpen(true);
      if (data.match_kind === "number") {
        toast.success(`Matched ${data.matched_job?.job_number} via job number — review and confirm`);
      } else if (data.match_kind === "name") {
        toast.info(`Possible match: ${data.matched_job?.title} (by name) — please review`);
      } else {
        toast.info("AI extracted the invoice — please pick the right job");
      }
    } catch (err) {
      toast.dismiss(toastId);
      const msg = err.response?.data?.detail || err.message || "Scan failed";
      toast.error(msg, { duration: 8000, description: <Link to={manualRoute} className="underline">Enter Manually Instead</Link> });
    } finally {
      setScanning(false);
    }
  };

  const onUpload = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    handleFile(file);
  };

  const ACCEPTED = /\.(pdf|png|jpe?g|heic|heif|webp)$/i;
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (scanning) return;
    const file = Array.from(e.dataTransfer?.files || [])[0];
    if (!file) return;
    if (!ACCEPTED.test(file.name)) { toast.error("PDF, PNG, JPG, HEIC or WEBP only"); return; }
    handleFile(file);
  };

  const saveInvoice = async (confirm = true) => {
    if (!reviewState || saving) return;
    if (confirm && !reviewState.job_id) { toast.error("Pick a job before confirming, or save for review"); return; }
    if (confirm && !reviewState.operational_reviewed) { toast.error("Check the extracted items against the original document first"); return; }
    setSaving(true);
    try {
      const ai = reviewState.ai;
      let saved;
      if (reviewState.id) {
        const editable = Object.fromEntries(["vendor", "invoice_number", "invoice_date", "due_date", "currency", "subtotal", "tax", "total", "po_number", "notes", "job_reference", "line_items"].filter((key) => key in ai).map((key) => [key, ai[key]]));
        saved = await api.patch(`/invoices/${reviewState.id}`, { ...editable, job_id: reviewState.job_id || null, confirm,
          operational_reviewed: reviewState.operational_reviewed });
      } else {
        saved = await api.post("/invoices", { ai, file: reviewState.file, job_id: reviewState.job_id || null,
          match_kind: confirm ? reviewState.match_kind : null, confirm, operational_reviewed: reviewState.operational_reviewed });
      }
      setPriceReviewInvoice({id:saved?.data?.id || reviewState.id, summary:saved?.data?.material_price_summary});
      toast.success(confirm ? "Invoice confirmed and linked to job cost" : "Invoice saved for review");
      setReviewOpen(false); setReviewState(null); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const removeInvoice = async (id) => {
    if (!window.confirm("Delete this invoice? It will be removed from the job cost.")) return;
    try {
      await api.delete(`/invoices/${id}`);
      toast.success("Invoice deleted");
      await load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const tok = typeof window !== "undefined" ? localStorage.getItem("fl_token") || "" : "";
  const fileLink = (path) => `${api.defaults.baseURL}/files/view?path=${encodeURIComponent(path)}&auth=${encodeURIComponent(tok)}&download=1`;

  return (
    <div className="p-6 lg:p-10 w-full pb-24 lg:pb-10" data-testid="invoices-page">
      {priceReviewInvoice?.id && <div className="mb-3 border rounded-md p-3 text-sm flex flex-wrap gap-2 items-center"><span>{priceReviewInvoice.summary?.unavailable ? "Invoice saved. Price review preparation needs a retry." : `Invoice saved · ${priceReviewInvoice.summary?.needs_review ?? 0} material prices to review.`}</span><Link className="text-[#FF5F15] font-semibold" to={`/app/materials?tab=price-updates&invoice_id=${encodeURIComponent(priceReviewInvoice.id)}`}>Review supplier prices →</Link></div>}
      {sourceJobId && <div className="mb-3 border border-orange-200 bg-orange-50 p-3 text-sm text-zinc-800">Adding materials to {jobs.find(j => j.id === sourceJobId)?.title || "this job"}. Review the document and job before confirming. <Link to={`/app/jobs/${sourceJobId}/costs`} className="text-[#FF5F15] font-semibold">Return to Materials &amp; Supplies →</Link></div>}
      <PageHeader
        tag="/ AI invoice scanner"
        title="Supplier invoices"
        subtitle="Scan a supplier document, review its items and deliveries, then link the approved cost to a job."
      />

      <div
        className={`hard-card p-4 bg-white dark:bg-[#101620] transition-all ${dragOver ? "ring-4 ring-white ring-offset-2 ring-offset-[#FF5F15] scale-[1.01]" : ""}`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false); }}
        onDrop={onDrop}
        data-testid="invoices-upload-card"
      >
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest opacity-90">
              <Sparkles className="w-3.5 h-3.5" /> Powered by Claude Sonnet 4.5 (vision)
            </div>
            <h2 className="font-display font-extrabold uppercase tracking-tight text-xl mt-1">
              {dragOver ? "Drop it here" : "Scan a new invoice"}
            </h2>
            <p className="text-sm opacity-90 mt-1 max-w-2xl">Drop a PDF or photo to extract invoice lines, dockets and delivery detail. Review the original before confirming. PDF, PNG, JPG, HEIC accepted, up to 12MB.</p>
          </div>
          <input
            ref={fileRef}
            type="file"
            accept=".pdf,application/pdf,image/png,image/jpeg,image/heic,image/heif,image/webp,.png,.jpg,.jpeg,.heic,.heif,.webp"
            className="hidden"
            onChange={onUpload}
            data-testid="invoices-file-input"
          />
          <div className="flex flex-wrap gap-2 items-center">
          <Button
            onClick={onPick}
            disabled={scanning}
            className="rounded-sm bg-[#09090B] hover:bg-black text-white uppercase tracking-wider font-bold shrink-0 h-12 px-6"
            data-testid="invoices-scan-button">
            {scanning ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Scanning…</>) : (<><Upload className="w-4 h-4 mr-2" /> Drop invoice</>)}
          </Button>
          <Button asChild variant="outline" className="h-12 rounded-sm"><Link to={manualRoute}>Enter Manually</Link></Button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mt-6">Loading…</div>
      ) : items.length === 0 ? (
        <div className="hard-card p-10 mt-6 text-center" data-testid="invoices-empty">
          <FileText className="w-12 h-12 mx-auto text-zinc-300 mb-3" strokeWidth={1.5} />
          <div className="font-display font-bold uppercase tracking-tight text-lg">No invoices yet</div>
          <div className="text-sm text-zinc-500 mt-1">Once you scan a supplier invoice it appears here and rolls into the matching job&apos;s cost.</div>
        </div>
      ) : (
        <div className="mt-6 hard-card overflow-x-auto" data-testid="invoices-list-card">
          <table className="fl-table w-full text-sm min-w-[720px]">
            <thead className="bg-[#09090B] text-white">
              <tr>
                {["Vendor", "Invoice #", "Date", "Job", "Total", "Status", ""].map((h) => (
                  <th key={h} className="text-left px-4 py-3 font-mono text-[11px] uppercase tracking-widest">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((inv) => (
                <tr key={inv.id} className="border-b border-zinc-200 hover:bg-zinc-50" data-testid={`invoice-row-${inv.id}`}>
                  <td className="px-4 py-3 font-medium">{inv.vendor || "—"}</td>
                  <td className="px-4 py-3 text-xs font-mono text-zinc-600">{inv.invoice_number || "—"}</td>
                  <td className="px-4 py-3 text-xs text-zinc-600">{inv.invoice_date || "—"}</td>
                  <td className="px-4 py-3">
                    {inv.job_number ? (
                      <a href={`/app/jobs/${inv.job_id}`} className="inline-flex items-center gap-1 bg-[#09090B] text-[#FF5F15] font-mono font-bold text-xs tracking-widest px-2 py-1 hover:bg-zinc-800">
                        {inv.job_number}
                      </a>
                    ) : (<span className="text-xs text-zinc-400">Unlinked</span>)}
                  </td>
                  <td className="px-4 py-3 font-display font-bold">{fmt(inv.total, inv.currency)}</td>
                  <td className="px-4 py-3"><StatusPill status={inv.status} /></td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button onClick={() => editInvoice(inv)} className="text-xs font-semibold text-[#FF5F15] px-2" data-testid={`invoice-review-${inv.id}`}>Review / edit</button>
                      {inv.file_path && (
                        <a href={fileLink(inv.file_path)} target="_blank" rel="noreferrer"
                           className="inline-flex items-center justify-center w-8 h-8 text-zinc-500 hover:text-[#FF5F15]"
                           data-testid={`invoice-view-${inv.id}`}
                           title="Open original file">
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                      <button onClick={() => removeInvoice(inv.id)}
                              className="inline-flex items-center justify-center w-8 h-8 text-zinc-500 hover:text-red-600"
                              data-testid={`invoice-delete-${inv.id}`}
                              title="Delete">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Review modal */}
      <Dialog open={reviewOpen} onOpenChange={(v) => { if (saving) return; setReviewOpen(v); if (!v) setReviewState(null); }}>
        <DialogContent className="w-[calc(100vw-24px)] max-w-4xl max-h-[92vh] overflow-y-auto rounded-lg border border-zinc-200 p-4 sm:p-6" data-testid="invoice-review-modal">
          <DialogHeader>
            <DialogTitle className="font-display font-black uppercase tracking-tight text-xl">
              Review & confirm
            </DialogTitle>
            <div className="text-xs text-zinc-500">Review supplier costs and delivery details against the original. Missing values stay blank.</div>
          </DialogHeader>

          {reviewState && (
            <div className="space-y-4">
              <OriginalInvoice path={reviewState.file?.path} />
              {reviewState.ai?.entry_method === "manual" && <label className="block text-xs text-[#FF5F15]">Attach supplier document
                <input type="file" disabled={saving} accept=".pdf,.png,.jpg,.jpeg,.heic,.heif,.webp" className="block mt-1" onChange={async (e) => {
                  const file = e.target.files?.[0]; e.target.value = "";
                  if (!file) return;
                  setSaving(true);
                  try {
                    const body = new FormData(); body.append("file", file); body.append("category", "supplier_invoice");
                    const uploaded = await api.post("/files/upload", body);
                    const {data} = await api.patch(`/invoices/${reviewState.id}`, {attachment_id: uploaded.data.id});
                    setReviewState(current => ({...current, file: {path: data.file_path}, ai: {...current.ai, file_path: data.file_path}}));
                    setItems(current => current.map(item => item.id === data.id ? data : item));
                    toast.success("Document attached");
                  } catch (error) { toast.error("Could not attach document. Please retry."); }
                  finally { setSaving(false); }
                }} />
              </label>}
              {(reviewState.ai?.review_fields?.length > 0 || (typeof reviewState.ai?.confidence === "number" && reviewState.ai.confidence < 0.8)) && <p className="text-xs text-amber-700 bg-amber-50 rounded p-2">Needs review · check {reviewState.ai?.review_fields?.join(", ").replaceAll("_", " ") || "unclear fields"} against the original document.</p>}
              {(reviewState.ai?.extraction_notes || []).map((note, i) => <p key={i} className="text-xs text-amber-700">{note}</p>)}
              {reviewState.match_kind === "number" && reviewState.matched_job && (
                <div className="bg-green-50 border-2 border-green-600 px-4 py-3 flex items-start gap-2 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                  <div>
                    <div className="font-bold text-green-900">Job number {reviewState.ai?.mentioned_job_number} found on the invoice</div>
                    <div className="text-green-800 text-xs">Suggested job <b>{reviewState.matched_job.job_number} · {reviewState.matched_job.title}</b></div>
                  </div>
                </div>
              )}
              {reviewState.match_kind === "name" && (
                <div className="bg-yellow-50 border-2 border-yellow-500 px-4 py-3 flex items-start gap-2 text-sm">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 shrink-0 mt-0.5" />
                  <div>
                    <div className="font-bold">Guessed by job name — please double-check</div>
                    <div className="text-xs text-yellow-900">Invoice mentioned &ldquo;{reviewState.ai?.mentioned_job_name}&rdquo; → suggested <b>{reviewState.matched_job?.title}</b></div>
                  </div>
                </div>
              )}
              {!reviewState.id && !reviewState.match_kind && (
                <div className="bg-zinc-100 border-2 border-zinc-400 px-4 py-3 flex items-start gap-2 text-sm">
                  <AlertTriangle className="w-5 h-5 text-zinc-600 shrink-0 mt-0.5" />
                  <div className="text-zinc-800">No job number or name detected on the invoice. Pick the right job below.</div>
                </div>
              )}

              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">Vendor</Label>
                  <Input className="rounded-none" value={reviewState.ai?.vendor || ""}
                         onChange={(e) => setReviewState({ ...reviewState, ai: { ...reviewState.ai, vendor: e.target.value } })}
                         aria-label="Vendor" data-testid="review-vendor" />
                </div>
                <div>
                  <Label className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">Invoice #</Label>
                  <Input className="rounded-none" value={reviewState.ai?.invoice_number || ""}
                         onChange={(e) => setReviewState({ ...reviewState, ai: { ...reviewState.ai, invoice_number: e.target.value } })}
                         aria-label="Invoice number" data-testid="review-invoice-number" />
                </div>
                <div>
                  <Label className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">Invoice date</Label>
                  <Input className="rounded-none" type="date" value={reviewState.ai?.invoice_date || ""}
                         onChange={(e) => setReviewState({ ...reviewState, ai: { ...reviewState.ai, invoice_date: e.target.value } })}
                         aria-label="Invoice date" data-testid="review-invoice-date" />
                </div>
                <div>
                  <Label className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">Total ({reviewState.ai?.currency || "NZD"})</Label>
                  <Input className="rounded-none" type="number" step="0.01" value={reviewState.ai?.total ?? ""}
                         onChange={(e) => setReviewState({ ...reviewState, ai: { ...reviewState.ai, total: e.target.value === "" ? null : Number(e.target.value) } })}
                         aria-label="Invoice total" data-testid="review-total" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {[["due_date", "Due date", "date"], ["po_number", "Purchase order", "text"], ["job_reference", "Document job reference", "text"], ["subtotal", "Subtotal", "number"], ["tax", "GST", "number"], ["currency", "Currency", "text"]].map(([key, label, type]) => <label key={key} className="text-[11px] uppercase tracking-wider text-zinc-500">{label}<Input aria-label={label} type={type} step={type === "number" ? "0.01" : undefined} value={reviewState.ai?.[key] ?? ""} onChange={(e) => setReviewState({ ...reviewState, ai: { ...reviewState.ai, [key]: e.target.value === "" ? null : type === "number" ? Number(e.target.value) : e.target.value } })} className="h-9 mt-1 text-xs" /></label>)}
              </div>
              <div>
                <Label className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">Link to job</Label>
                <Select value={reviewState.job_id || "unassigned"} onValueChange={(v) => setReviewState({ ...reviewState, job_id: v === "unassigned" ? "" : v })}>
                  <SelectTrigger className="rounded-none" data-testid="review-job-select"><SelectValue placeholder="Pick a job" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned · save for review</SelectItem>
                    {jobs.map((j) => (
                      <SelectItem key={j.id} value={j.id}>
                        {j.job_number ? `${j.job_number} · ` : ""}{j.title}{j.client_name ? ` — ${j.client_name}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <section className="border-t border-zinc-200 pt-3">
                <h3 className="text-[11px] font-bold uppercase tracking-widest text-zinc-500 mb-3">Extracted items / deliveries</h3>
                <SupplierItems items={reviewState.ai?.line_items || []} currency={reviewState.ai?.currency} fleet={fleet} matches={reviewState.ai?.fleet_matches || {}} reviewed={reviewState.operational_reviewed} onChange={(line_items) => setReviewState({ ...reviewState, operational_reviewed: false, ai: { ...reviewState.ai, line_items } })} />
              </section>
              <InvoiceMaterialPrices invoice={reviewState.ai} invoiceId={reviewState.id} />
              {reviewState.original?.line_items?.length > 0 && <details className="text-xs border-t border-zinc-200 pt-2">
                <summary className="cursor-pointer text-zinc-500">Original stored extraction · compare before confirming</summary>
                <div className="mt-2"><SupplierItems items={reviewState.original.line_items} currency={reviewState.original.currency} matches={reviewState.ai?.fleet_matches || {}} /></div>
              </details>}
              <label className="flex items-start gap-2 text-xs border-t border-zinc-200 pt-3">
                <input type="checkbox" checked={!!reviewState.operational_reviewed} onChange={(e) => setReviewState({ ...reviewState, operational_reviewed: e.target.checked })} className="mt-0.5 accent-[#FF5F15]" data-testid="review-operational-confirm" />
                {reviewState.ai?.entry_method === "manual" ? "I checked the manually entered items, quantities, units and delivery details." : "I checked the extracted items, quantities, units and delivery details against the original document. Unknown values remain blank."}
              </label>
              <p className="text-[11px] text-zinc-500">Supplier costs only. Customer sell rates and invoice lines remain under your control.</p>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button disabled={saving} variant="outline" onClick={() => { setReviewOpen(false); setReviewState(null); }} className="rounded-none" data-testid="review-cancel">
              Cancel
            </Button>
            {(!reviewState?.id || reviewState?.ai?.status === "pending_review") && <Button disabled={saving} variant="outline" onClick={() => saveInvoice(false)}>Save for review</Button>}
            <Button disabled={saving || !reviewState?.operational_reviewed || !reviewState?.job_id} onClick={() => saveInvoice(true)} className="bg-[#FF5F15] hover:bg-[#E65100] text-white rounded-none font-bold uppercase tracking-wider" data-testid="review-save">
              <Wand2 className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Confirm & link to job"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
